import { useState, memo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import {
  Box,
  Flex,
  Heading,
  Text,
  HStack,
  Button,
  Dialog,
  CloseButton,
  VStack,
} from "@libs/design";
import { Maximize2 } from "@assets/icons";

const TICK_FONT_SIZE = 12;
const TICK_COLOR = "#6B7280";
const TICK_FONT_WEIGHT = 500;

const XTick = (props: {
  x?: number;
  y?: number;
  payload?: { value: string; index?: number };
  formattedLabels?: string[];
}) => {
  const { x = 0, y = 0, payload, formattedLabels } = props;
  const text = formattedLabels?.[payload?.index ?? -1] ?? payload?.value ?? "";
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={12}
        textAnchor="middle"
        fill={TICK_COLOR}
        style={{ fontSize: TICK_FONT_SIZE, fontWeight: TICK_FONT_WEIGHT }}
      >
        {text}
      </text>
    </g>
  );
};

const YTick = (props: {
  x?: number;
  y?: number;
  payload?: { value: number | string };
  textAnchor?: string;
}) => {
  const { x = 0, y = 0, payload } = props;
  const val = payload?.value;
  const text = typeof val === "number" ? `${val}%` : String(val ?? "");
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={4}
        textAnchor="end"
        fill={TICK_COLOR}
        style={{ fontSize: TICK_FONT_SIZE, fontWeight: TICK_FONT_WEIGHT }}
      >
        {text}
      </text>
    </g>
  );
};

const seriesColors: Record<string, string> = {
  Overall: "#3B82F6",
  Sales: "#10B981",
  Purchase: "#F59E0B",
  Stock: "#8B5CF6",
};

const TrendChartTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    payload?: { year?: string; month?: string };
  }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  const period = payload[0]?.payload?.year
    ? `${label} ${String(payload[0].payload.year).slice(-2)}`
    : (label ?? "");
  return (
    <Box
      bg="white"
      borderRadius="md"
      borderWidth="1px"
      borderColor="gray.200"
      boxShadow="md"
      px={3}
      py={2.5}
      minW="120px"
    >
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="gray.700"
        mb={2}
        style={{ fontSize: "11px" }}
      >
        {period}
      </Text>
      <VStack gap={1} align="stretch">
        {payload.map((entry) => (
          <Flex key={entry.name} justify="space-between" align="center" gap={3}>
            <HStack gap={1.5}>
              <Box
                w={2}
                h={2}
                borderRadius="full"
                style={{
                  backgroundColor: entry.color || seriesColors[entry.name] || "#6B7280",
                }}
              />
              <Text fontSize="xs" color="gray.600" style={{ fontSize: "10px" }}>
                {entry.name}
              </Text>
            </HStack>
            <Text fontSize="xs" fontWeight="semibold" color="gray.900" style={{ fontSize: "10px" }}>
              {entry.value}%
            </Text>
          </Flex>
        ))}
      </VStack>
    </Box>
  );
};

export type DQTrendChartRow = {
  month: string;
  year?: string;
  overall: number;
  sales: number;
  purchase: number;
  stock: number;
};

export type DQTrendChartProps = {
  data: DQTrendChartRow[];
};

export const DQTrendChart = memo(function DQTrendChart({ data }: DQTrendChartProps) {
  const [selectedFilter, setSelectedFilter] = useState<"6M" | "1Y" | "ALL">("6M");
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleSeries, setVisibleSeries] = useState({
    overall: true,
    sales: true,
    purchase: true,
    stock: true,
  });

  const getFilteredData = (): DQTrendChartRow[] => {
    const n = data.length;
    switch (selectedFilter) {
      case "6M":
        return n > 6 ? data.slice(-6) : data;
      case "1Y":
        return n > 12 ? data.slice(-12) : data;
      case "ALL":
        return data;
      default:
        return data;
    }
  };

  const filteredData = getFilteredData();

  const allValues = filteredData
    .flatMap((d) => [d.overall, d.sales, d.purchase, d.stock])
    .filter((v) => v != null && v > 0);
  const minVal = allValues.length > 0 ? Math.min(...allValues) : 0;
  const maxVal = allValues.length > 0 ? Math.max(...allValues) : 100;
  const yMin = Math.max(0, Math.floor(minVal / 10) * 10 - 5);
  const yMax = Math.min(100, Math.ceil(maxVal / 10) * 10 + 5);
  const yDomain = [yMin, Math.max(yMax, yMin + 10)];
  const subtitleText =
    selectedFilter === "6M"
      ? "Last 6 months from latest data (by domain)"
      : selectedFilter === "1Y"
        ? "Last 12 months from latest data (by domain)"
        : "All available data (by domain)";

  const colors = {
    activeBg: "#D1FAE5",
    activeText: "#00927B",
    inactiveText: "#6B7280",
    hoverBg: "#F3F4F6",
  };

  const seriesColors = {
    overall: { active: "#3B82F6", bg: "#DBEAFE", text: "#1E40AF" },
    sales: { active: "#10B981", bg: "#D1FAE5", text: "#047857" },
    purchase: { active: "#F59E0B", bg: "#FEF3C7", text: "#B45309" },
    stock: { active: "#8B5CF6", bg: "#dcd7f5", text: "#4f0db9" },
  };

  const toggleSeries = (series: keyof typeof visibleSeries) => {
    setVisibleSeries((prev) => ({
      ...prev,
      [series]: !prev[series],
    }));
  };

  const renderChart = (height: number) => (
    <>
      <Box w="100%" h={height} pr={1}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={filteredData} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
            <defs>
              <linearGradient id="colorOverall" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563EB" stopOpacity={0.8} />
                <stop offset="30%" stopColor="#3B82F6" stopOpacity={0.6} />
                <stop offset="70%" stopColor="#60A5FA" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#93C5FD" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#059669" stopOpacity={0.8} />
                <stop offset="30%" stopColor="#10B981" stopOpacity={0.6} />
                <stop offset="70%" stopColor="#34D399" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#6EE7B7" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorPurchase" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D97706" stopOpacity={0.8} />
                <stop offset="30%" stopColor="#F59E0B" stopOpacity={0.6} />
                <stop offset="70%" stopColor="#FBBF24" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#FCD34D" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorStock" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.8} />
                <stop offset="30%" stopColor="#8B5CF6" stopOpacity={0.6} />
                <stop offset="70%" stopColor="#A78BFA" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#C4B5FD" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="month"
              tick={
                <XTick
                  formattedLabels={filteredData.map((d) =>
                    d.year ? `${d.month} ${d.year.slice(-2)}` : d.month
                  )}
                />
              }
              stroke="#9CA3AF"
            />
            <YAxis domain={yDomain} tick={<YTick />} stroke="#9CA3AF" width={44} tickMargin={8} />
            <Tooltip content={<TrendChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: "12px" }} />
            {visibleSeries.overall && (
              <Area
                type="monotone"
                dataKey="overall"
                stroke="#3B82F6"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorOverall)"
                name="Overall"
              />
            )}
            {visibleSeries.sales && (
              <Area
                type="monotone"
                dataKey="sales"
                stroke="#10B981"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorSales)"
                name="Sales"
              />
            )}
            {visibleSeries.purchase && (
              <Area
                type="monotone"
                dataKey="purchase"
                stroke="#F59E0B"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorPurchase)"
                name="Purchase"
              />
            )}
            {visibleSeries.stock && (
              <Area
                type="monotone"
                dataKey="stock"
                stroke="#8B5CF6"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorStock)"
                name="Stock"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </>
  );

  return (
    <>
      <Box p={6}>
        <Flex justify="space-between" align="center" mb={6}>
          <Box gap={4}>
            <Heading fontSize="md" fontWeight="semibold" color="gray.900" textWrap="nowrap">
              Data Quality Trend
            </Heading>
            <Text fontSize="xs" color="gray.500">
              {subtitleText}
            </Text>
          </Box>
          <HStack gap={3}>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => setIsExpanded(true)}
              style={{ color: "#6B7280" }}
              _hover={{ backgroundColor: "#F3F4F6" }}
              _focus={{ boxShadow: "none", outline: "none" }}
              _focusVisible={{ boxShadow: "none", outline: "none" }}
            >
              <Maximize2 size={16} />
            </Button>
            <Button
              size="sm"
              fontSize="xs"
              fontWeight="medium"
              px={3}
              py={1.5}
              borderRadius="md"
              onClick={() => setSelectedFilter("6M")}
              style={{
                backgroundColor: selectedFilter === "6M" ? colors.activeBg : "transparent",
                color: selectedFilter === "6M" ? colors.activeText : colors.inactiveText,
              }}
              _hover={{
                bg: selectedFilter === "6M" ? colors.activeBg : colors.hoverBg,
              }}
            >
              6M
            </Button>
            <Button
              size="sm"
              fontSize="xs"
              fontWeight="medium"
              px={3}
              py={1.5}
              borderRadius="md"
              onClick={() => setSelectedFilter("1Y")}
              style={{
                backgroundColor: selectedFilter === "1Y" ? colors.activeBg : "transparent",
                color: selectedFilter === "1Y" ? colors.activeText : colors.inactiveText,
              }}
              _hover={{
                bg: selectedFilter === "1Y" ? colors.activeBg : colors.hoverBg,
              }}
            >
              1Y
            </Button>
            <Button
              size="sm"
              fontSize="xs"
              fontWeight="medium"
              px={3}
              py={1.5}
              borderRadius="md"
              onClick={() => setSelectedFilter("ALL")}
              style={{
                backgroundColor: selectedFilter === "ALL" ? colors.activeBg : "transparent",
                color: selectedFilter === "ALL" ? colors.activeText : colors.inactiveText,
              }}
              _hover={{
                bg: selectedFilter === "ALL" ? colors.activeBg : colors.hoverBg,
              }}
            >
              ALL
            </Button>
          </HStack>
        </Flex>
        <HStack gap={2} mb={4} flexWrap="wrap">
          <Button
            size="xs"
            fontSize="xs"
            fontWeight="medium"
            px={2.5}
            py={1}
            borderRadius="md"
            onClick={() => toggleSeries("overall")}
            style={{
              backgroundColor: visibleSeries.overall ? seriesColors.overall.bg : "#F3F4F6",
              color: visibleSeries.overall ? seriesColors.overall.text : "#9CA3AF",
              border: visibleSeries.overall ? "none" : "1px solid #E5E7EB",
            }}
            _hover={{
              backgroundColor: visibleSeries.overall ? seriesColors.overall.bg : "#E5E7EB",
            }}
          >
            Overall
          </Button>
          <Button
            size="xs"
            fontSize="xs"
            fontWeight="medium"
            px={2.5}
            py={1}
            borderRadius="md"
            onClick={() => toggleSeries("sales")}
            style={{
              backgroundColor: visibleSeries.sales ? seriesColors.sales.bg : "#F3F4F6",
              color: visibleSeries.sales ? seriesColors.sales.text : "#9CA3AF",
              border: visibleSeries.sales ? "none" : "1px solid #E5E7EB",
            }}
            _hover={{
              backgroundColor: visibleSeries.sales ? seriesColors.sales.bg : "#E5E7EB",
            }}
          >
            Sales
          </Button>
          <Button
            size="xs"
            fontSize="xs"
            fontWeight="medium"
            px={2.5}
            py={1}
            borderRadius="md"
            onClick={() => toggleSeries("purchase")}
            style={{
              backgroundColor: visibleSeries.purchase ? seriesColors.purchase.bg : "#F3F4F6",
              color: visibleSeries.purchase ? seriesColors.purchase.text : "#9CA3AF",
              border: visibleSeries.purchase ? "none" : "1px solid #E5E7EB",
            }}
            _hover={{
              backgroundColor: visibleSeries.purchase ? seriesColors.purchase.bg : "#E5E7EB",
            }}
          >
            Purchase
          </Button>
          <Button
            size="xs"
            fontSize="xs"
            fontWeight="medium"
            px={2.5}
            py={1}
            borderRadius="md"
            onClick={() => toggleSeries("stock")}
            style={{
              backgroundColor: visibleSeries.stock ? seriesColors.stock.bg : "#F3F4F6",
              color: visibleSeries.stock ? seriesColors.stock.text : "#9CA3AF",
              border: visibleSeries.stock ? "none" : "1px solid #E5E7EB",
            }}
            _hover={{
              backgroundColor: visibleSeries.stock ? seriesColors.stock.bg : "#E5E7EB",
            }}
          >
            Stock
          </Button>
        </HStack>
        {renderChart(280)}
      </Box>

      <Dialog.Root open={isExpanded} onOpenChange={(e) => setIsExpanded(e.open)}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="7xl" style={{ backgroundColor: "#FFFFFF" }}>
            <Dialog.Header>
              <Dialog.Title style={{ color: "#111827", fontSize: "1rem", fontWeight: 600 }}>
                Data Quality Trend
              </Dialog.Title>
              <Dialog.CloseTrigger>
                <CloseButton bg="transparent" />
              </Dialog.CloseTrigger>{" "}
            </Dialog.Header>
            <Dialog.Body>
              <Box mb={4}>
                <HStack gap={3} mb={3}>
                  <Button
                    size="sm"
                    fontSize="xs"
                    fontWeight="medium"
                    px={3}
                    py={1.5}
                    borderRadius="md"
                    onClick={() => setSelectedFilter("6M")}
                    style={{
                      backgroundColor: selectedFilter === "6M" ? colors.activeBg : "transparent",
                      color: selectedFilter === "6M" ? colors.activeText : colors.inactiveText,
                    }}
                    _hover={{
                      bg: selectedFilter === "6M" ? colors.activeBg : colors.hoverBg,
                    }}
                  >
                    6M
                  </Button>
                  <Button
                    size="sm"
                    fontSize="xs"
                    fontWeight="medium"
                    px={3}
                    py={1.5}
                    borderRadius="md"
                    onClick={() => setSelectedFilter("1Y")}
                    style={{
                      backgroundColor: selectedFilter === "1Y" ? colors.activeBg : "transparent",
                      color: selectedFilter === "1Y" ? colors.activeText : colors.inactiveText,
                    }}
                    _hover={{
                      bg: selectedFilter === "1Y" ? colors.activeBg : colors.hoverBg,
                    }}
                  >
                    1Y
                  </Button>
                  <Button
                    size="sm"
                    fontSize="xs"
                    fontWeight="medium"
                    px={3}
                    py={1.5}
                    borderRadius="md"
                    onClick={() => setSelectedFilter("ALL")}
                    style={{
                      backgroundColor: selectedFilter === "ALL" ? colors.activeBg : "transparent",
                      color: selectedFilter === "ALL" ? colors.activeText : colors.inactiveText,
                    }}
                    _hover={{
                      bg: selectedFilter === "ALL" ? colors.activeBg : colors.hoverBg,
                    }}
                  >
                    ALL
                  </Button>
                </HStack>
                <HStack gap={2} flexWrap="wrap">
                  <Button
                    size="xs"
                    fontSize="xs"
                    fontWeight="medium"
                    px={2.5}
                    py={1}
                    borderRadius="md"
                    onClick={() => toggleSeries("overall")}
                    style={{
                      backgroundColor: visibleSeries.overall ? seriesColors.overall.bg : "#F3F4F6",
                      color: visibleSeries.overall ? seriesColors.overall.text : "#9CA3AF",
                      border: visibleSeries.overall ? "none" : "1px solid #E5E7EB",
                    }}
                    _hover={{
                      backgroundColor: visibleSeries.overall ? seriesColors.overall.bg : "#E5E7EB",
                    }}
                  >
                    Overall
                  </Button>
                  <Button
                    size="xs"
                    fontSize="xs"
                    fontWeight="medium"
                    px={2.5}
                    py={1}
                    borderRadius="md"
                    onClick={() => toggleSeries("sales")}
                    style={{
                      backgroundColor: visibleSeries.sales ? seriesColors.sales.bg : "#F3F4F6",
                      color: visibleSeries.sales ? seriesColors.sales.text : "#9CA3AF",
                      border: visibleSeries.sales ? "none" : "1px solid #E5E7EB",
                    }}
                    _hover={{
                      backgroundColor: visibleSeries.sales ? seriesColors.sales.bg : "#E5E7EB",
                    }}
                  >
                    Sales
                  </Button>
                  <Button
                    size="xs"
                    fontSize="xs"
                    fontWeight="medium"
                    px={2.5}
                    py={1}
                    borderRadius="md"
                    onClick={() => toggleSeries("purchase")}
                    style={{
                      backgroundColor: visibleSeries.purchase
                        ? seriesColors.purchase.bg
                        : "#F3F4F6",
                      color: visibleSeries.purchase ? seriesColors.purchase.text : "#9CA3AF",
                      border: visibleSeries.purchase ? "none" : "1px solid #E5E7EB",
                    }}
                    _hover={{
                      backgroundColor: visibleSeries.purchase
                        ? seriesColors.purchase.bg
                        : "#E5E7EB",
                    }}
                  >
                    Purchase
                  </Button>
                  <Button
                    size="xs"
                    fontSize="xs"
                    fontWeight="medium"
                    px={2.5}
                    py={1}
                    borderRadius="md"
                    onClick={() => toggleSeries("stock")}
                    style={{
                      backgroundColor: visibleSeries.stock ? seriesColors.stock.bg : "#F3F4F6",
                      color: visibleSeries.stock ? seriesColors.stock.text : "#9CA3AF",
                      border: visibleSeries.stock ? "none" : "1px solid #E5E7EB",
                    }}
                    _hover={{
                      backgroundColor: visibleSeries.stock ? seriesColors.stock.bg : "#E5E7EB",
                    }}
                  >
                    Stock
                  </Button>
                </HStack>
              </Box>
              {renderChart(500)}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </>
  );
});
